<template>
  <div class="video-chapter-container">
    <!-- 顶部头部 -->
    <div class="page-header">
      <div class="header-left">
        <i class="el-icon-back back-icon" @click="goBack"></i>
        <span class="title">{{ readonly ? '课程详情' : '章节管理' }}</span>
      </div>
      <div class="header-right" v-if="!readonly">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddChapter">添加章节</el-button>
      </div>
    </div>

    <!-- 课程基本信息 -->
    <div class="course-info" v-if="courseInfo">
      <div class="course-cover">
        <img v-if="courseInfo.coverUrl" :src="resolveCoverUrl(courseInfo.coverUrl)" class="cover-image" alt="课程封面" />
        <div v-else class="cover-placeholder">
          <span>{{ courseInfo.coverText || courseInfo.courseName.charAt(0) }}</span>
        </div>
      </div>
      <div class="course-meta">
        <div class="course-name">{{ courseInfo.courseName }}</div>
        <div class="course-row">
          <el-tag size="mini" type="info" class="course-type-tag">{{ courseInfo.courseType }}</el-tag>
        </div>
        <div class="course-stats">
          <span class="stat-item">
            <i class="el-icon-user"></i>
            讲师：{{ courseInfo.instructor }}
          </span>
          <span class="stat-item">
            <i class="el-icon-time"></i>
            {{ courseInfo.createTime }}
          </span>
        </div>
        <div v-if="courseInfo.description" class="course-desc" :title="courseInfo.description">
          {{ courseInfo.description }}
        </div>
      </div>
    </div>

    <!-- 章节目录 -->
    <div class="chapter-section">
      <div class="section-header">
        <i class="el-icon-tickets"></i>
        <span>章节目录</span>
        <span class="count">(共 {{ chapterList.length }} 节)</span>
      </div>
      <div class="chapter-list">
        <div
          v-for="(item, index) in chapterList"
          :key="item.id"
          class="chapter-item"
        >
          <div class="chapter-index">{{ String(index + 1).padStart(2, '0') }}</div>
          <div
            class="chapter-thumb"
            :class="{ 'no-video': !item.videoUrl }"
            @click="handlePlay(item)"
          >
            <i v-if="item.videoUrl" class="el-icon-video-play play-icon"></i>
            <span v-else class="thumb-empty">未上传视频</span>
          </div>
          <div class="chapter-content">
            <div class="chapter-title">{{ item.title }}</div>
            <div v-if="item.description" class="chapter-desc">{{ item.description }}</div>
          </div>
          <div v-if="item.duration && item.duration !== '--'" class="chapter-duration">{{ item.duration }}</div>
          <i
            class="el-icon-document materials-icon-btn"
            :class="{ disabled: !item.materials || item.materials.length === 0 }"
            @click="handleViewMaterials(item)"
            title="查看课件"
          ></i>
          <i
            class="el-icon-video-play play-icon-btn"
            :class="{ disabled: !item.videoUrl }"
            @click="handlePlay(item)"
            :title="item.videoUrl ? '播放' : '暂无视频'"
          ></i>
          <div class="chapter-actions" v-if="!readonly">
            <el-button type="text" size="mini" @click="handleEditChapter(item)">编辑</el-button>
            <el-divider direction="vertical"></el-divider>
            <el-button type="text" size="mini" style="color: #f56c6c;" @click="handleDeleteChapter(item)">删除</el-button>
          </div>
        </div>
        <div v-if="chapterList.length === 0" class="empty-tip">
          <i class="el-icon-folder-opened"></i>
          <p>{{ readonly ? '暂无章节' : '暂无章节，点击"添加章节"开始创建' }}</p>
        </div>
      </div>
    </div>

    <!-- 新增/编辑章节弹窗 -->
    <VideoChapterAdd
      :visible.sync="addChapterDialogVisible"
      :edit-data="editChapter"
      :course-id="courseId"
      :next-chapter-no="nextChapterNo"
      :next-section-no="nextSectionNo"
      @saved="onChapterSaved"
    />

    <!-- 查看课件弹窗 -->
    <el-dialog
      :title="materialsDialogTitle"
      :visible.sync="materialsDialogVisible"
      width="600px"
      top="5%"
      class="materials-dialog"
      @close="onMaterialsDialogClose"
    >
      <div v-if="currentMaterials && currentMaterials.length > 0" class="materials-list">
        <div v-for="(m, idx) in currentMaterials" :key="idx" class="materials-item">
          <i class="el-icon-document"></i>
          <span class="materials-name">{{ m.materialName || m.name || m.fileName || '课件' }}</span>
          <a
            class="materials-download"
            :href="m.downloadUrl || m.fileUrl"
            :download="m.materialName || m.name || m.fileName"
            target="_blank"
            rel="noopener"
          >
            <i class="el-icon-download"></i>
            下载
          </a>
        </div>
      </div>
      <el-empty v-else description="该章节暂无课件" :image-size="60" />
    </el-dialog>
  </div>
</template>

<script>
import VideoChapterAdd from './VideoChapterAdd.vue'
import { getChapterList, deleteChapter } from '@/api/video/chapter'

export default {
  name: 'VideoChapter',
  components: { VideoChapterAdd },
  computed: {
    readonly() {
      return this.$route.query.readonly === 'true'
    },
    nextChapterNo() {
      // 章编号默认从 1 开始
      return 1
    },
    nextSectionNo() {
      // 节编号默认 = 当前章节数 + 1
      return (this.chapterList && this.chapterList.length
        ? this.chapterList.length
        : 0) + 1
    },
    materialsDialogTitle() {
      return `课件列表：${this.currentChapterTitle || ''}`
    },
  },
  created() {
    const { courseId } = this.$route.query
    if (courseId) {
      this.courseId = Number(courseId)
      this.initCourseInfo()
      this.loadChapters()
    }
  },
  data() {
    return {
      courseId: null,
      courseInfo: {
        courseName: '',
        courseType: '',
        instructor: '',
        createTime: '',
        description: '',
        coverText: '',
      },
      chapterList: [],
      addChapterDialogVisible: false,
      materialsDialogVisible: false,
      currentMaterials: [],
      currentChapterTitle: '',
      editChapter: null,
    }
  },
  methods: {
    goBack() {
      this.$router.back()
    },
    initCourseInfo() {
      const { courseName, courseType, instructor, createTime, coverUrl, description } = this.$route.query
      this.courseInfo = {
        courseName: courseName || '',
        courseType: courseType || '',
        instructor: instructor || '',
        createTime: createTime || '',
        coverUrl: coverUrl || '',
        description: description || '',
        coverText: (courseName || '').charAt(0),
      }
    },
    resolveCoverUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      let path = url.startsWith('/') ? url : `/${url}`
      path = path.replace(/^\/home\/file_upload/, '/files')
      const base = (
        this.$store.state.IPAss.yl ||
        process.env.VUE_APP_PREVIEW_FILE_URL ||
        ''
      ).replace(/\/$/, '')
      return `${base}${path}`
    },
    async loadChapters() {
      try {
        const res = await getChapterList(this.courseId)
        if (res && res.code === 200) {
          const data = res.data || {}
          const list = Array.isArray(data)
            ? data
            : (data.chapters || data.chapterList || data.list || [])
          this.chapterList = list.map(item => {
            const video = item.video || {}
            const materials = item.materials || []
            return {
              id: item.id,
              title: item.sectionName || item.title || item.name || '',
              description: video.videoName || '',
              duration: video.duration ? this.formatDuration(video.duration) : '--',
              videoUrl: video.videoUrl || '',
              videoName: video.videoName || '',
              materials: materials.map(m => ({
                id: m.id,
                materialName: m.materialName || '',
                name: m.materialName || '',
                fileName: m.materialName || '',
                downloadUrl: m.downloadUrl || m.fileUrl || '',
                fileUrl: m.fileUrl || '',
                url: m.fileUrl || '',
                previewUrl: m.previewUrl || m.fileUrl || '',
                fileType: m.fileType || '',
              })),
              chapterNo: item.chapterNo,
              sectionNo: item.sectionNo,
              sortOrder: item.sortOrder,
              status: item.status,
            }
          })
          this.total = this.chapterList.length
        }
      } catch (e) {
        console.error('加载章节目录失败', e)
      }
    },
    formatDuration(seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    handleAddChapter() {
      this.editChapter = null
      this.addChapterDialogVisible = true
    },
    handleEditChapter(item) {
      this.editChapter = item
      this.addChapterDialogVisible = true
    },
    onChapterSaved() {
      this.loadChapters()
    },
    handleViewMaterials(item) {
      this.currentMaterials = (item && item.materials) || []
      this.currentChapterTitle = item.title || ''
      this.materialsDialogVisible = true
    },
    onMaterialsDialogClose() {
      this.materialsDialogVisible = false
      this.currentMaterials = []
      this.currentChapterTitle = ''
    },
    async handleDeleteChapter(item) {
      try {
        await this.$confirm(`确定要删除章节"${item.title}"吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
        const res = await deleteChapter(item.id)
        if (res.code === 200 && res.data) {
          this.$message.success('删除成功')
          this.loadChapters()
        }
      } catch (e) {
        // 取消操作
      }
    },
    handlePlay(item) {
      if (!item.videoUrl) {
        this.$message.warning('该章节暂无可播放视频')
        return
      }
      this.$router.push({
        path: '/video/VideoPlayer',
        query: {
          courseId: this.courseId,
          courseName: this.courseInfo.courseName,
          courseType: this.courseInfo.courseType,
          instructor: this.courseInfo.instructor,
          createTime: this.courseInfo.createTime,
          description: this.courseInfo.description,
          chapterId: item.id,
        },
      })
    },
  },
}
</script>

<style lang="scss">
/* 隐藏页面级滚动条，仅保留内部章节区域滚动 */
html, body, #app, .el-main {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
html::-webkit-scrollbar,
body::-webkit-scrollbar,
#app::-webkit-scrollbar,
.el-main::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}
</style>

<style scoped lang="scss">
.video-chapter-container {
  padding: 16px 20px;
  background: #f0f2f5;
  height: calc(100vh - 130px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 顶部头部 */
.page-header {
  background: #fff;
  padding: 12px 20px;
  border-radius: 6px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;

    .back-icon {
      font-size: 20px;
      color: #606266;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s;

      &:hover {
        background: #f5f7fa;
        color: #409eff;
      }
    }

    .title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }
}

/* 课程基本信息 */
.course-info {
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  display: flex;
  gap: 24px;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .course-cover {
    flex-shrink: 0;

    .cover-image {
      width: 140px;
      height: 140px;
      object-fit: cover;
      border-radius: 8px;
      display: block;
    }

    .cover-placeholder {
      width: 140px;
      height: 140px;
      background: linear-gradient(135deg, #e7f8ee 0%, #b3e5ca 100%);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        font-size: 36px;
        font-weight: 600;
        color: #42b883;
      }
    }
  }

  .course-meta {
    flex: 1;
    min-width: 0;

    .course-name {
      font-size: 20px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 12px;
    }

    .course-row {
      margin-bottom: 14px;

      .course-type-tag {
        background: linear-gradient(135deg, #e6f0ff 0%, #d9e8ff 100%) !important;
        color: #409eff !important;
        border: none !important;
        font-size: 12px !important;
        padding: 2px 10px !important;
        height: 22px !important;
        line-height: 18px !important;
        font-weight: 500;
        border-radius: 3px !important;
      }
    }

    .course-stats {
      display: flex;
      gap: 24px;

      .stat-item {
        display: flex;
        align-items: center;
        font-size: 13px;
        color: #606266;

        i {
          margin-right: 4px;
          color: #909399;
        }
      }
    }

    .course-desc {
      margin-top: 6px;
      font-size: 13px;
      color: #606266;
      line-height: 1.6;
      word-break: break-all;
      white-space: pre-wrap;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

/* 章节目录 */
.chapter-section {
  background: #fff;
  border-radius: 6px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  .section-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #ebeef5;

    i {
      color: #409eff;
    }

    .count {
      font-weight: normal;
      font-size: 13px;
      color: #909399;
      margin-left: 4px;
    }
  }
}

.chapter-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chapter-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    border-color: #c6e2ff;
    background: #f5fbff;
  }

  .chapter-index {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .chapter-thumb {
    width: 90px;
    height: 60px;
    background: linear-gradient(135deg, #d6e9ff 0%, #b3d8ff 100%);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s;

    &:hover {
      background: linear-gradient(135deg, #b3d8ff 0%, #7fb8ff 100%);
    }

    &.no-video {
      background: #f5f7fa;
      cursor: not-allowed;

      .play-icon {
        display: none;
      }
    }

    .play-icon {
      font-size: 22px;
      color: #fff;
    }

    .thumb-empty {
      font-size: 12px;
      color: #c0c4cc;
      text-align: center;
      line-height: 1.3;
      padding: 0 4px;
    }
  }

  .chapter-content {
    flex: 1;
    min-width: 0;

    .chapter-title {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
      margin-bottom: 4px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .chapter-desc {
      font-size: 12px;
      color: #909399;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .chapter-duration {
    font-size: 13px;
    color: #606266;
    width: 50px;
    text-align: center;
    flex-shrink: 0;
  }

  .play-icon-btn {
    flex-shrink: 0;
    margin: 0 8px;
    font-size: 26px;
    color: #409eff;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.2);
    }

    &.disabled {
      color: #c0c4cc;
      cursor: not-allowed;

      &:hover {
        transform: none;
      }
    }
  }

  .materials-icon-btn {
    flex-shrink: 0;
    margin: 0 8px;
    font-size: 22px;
    color: #67c23a;
    cursor: pointer;
    transition: transform 0.2s;

    &.disabled {
      color: #c0c4cc;
      cursor: not-allowed;
    }

    &:not(.disabled):hover {
      transform: scale(1.2);
    }
  }

  .chapter-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
}

.empty-tip {
  text-align: center;
  padding: 40px 0;
  color: #c0c4cc;

  i {
    font-size: 40px;
    margin-bottom: 8px;
  }

  p {
    font-size: 13px;
  }
}

.materials-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 500px;
  overflow-y: auto;
}

.materials-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #ecf5ff;
  }

  > i.el-icon-document {
    font-size: 20px;
    color: #409eff;
    flex-shrink: 0;
  }

  .materials-name {
    flex: 1;
    font-size: 14px;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .materials-download {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    font-size: 12px;
    color: #fff;
    background: #409eff;
    border-radius: 3px;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.2s;
    flex-shrink: 0;

    &:hover {
      background: #66b1ff;
    }
  }
}
</style>
